// ContactForm.tsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { Contact } from '../../models';
import { v4 as uuidv4 } from 'uuid';

interface ContactFormProps {
  onSave: (contact: Contact) => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ onSave }) => {
  // Remove "active" from the form data since we default it to true.
  const [formData, setFormData] = useState<Omit<Contact, 'id' | 'interactions' | 'active'>>({
    name: '',
    phoneNumber: '',
    email: '',
    address: '',
    howDidWeMeet: '',
    bibleStudy: false,
    baptism: false,
    pointOfContact: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newContact: Contact = {
      id: uuidv4(),
      ...formData,
      active: true, // Always default active to true.
      interactions: [],
    };

    onSave(newContact);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}
    >
      {/* Basic Information */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6">Basic Information</Typography>
        <TextField
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />
        <TextField
          label="Phone Number"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handleChange}
        />
        <TextField
          label="Email"
          name="email"
          value={formData.email}
          onChange={handleChange}
        />
        <TextField
          label="Address"
          name="address"
          value={formData.address}
          onChange={handleChange}
        />
      </Box>

      {/* Additional Details */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6">Additional Details</Typography>
        <TextField
          label="How did we meet?"
          name="howDidWeMeet"
          value={formData.howDidWeMeet}
          onChange={handleChange}
        />
        <TextField
          label="Point of Contact"
          name="pointOfContact"
          value={formData.pointOfContact}
          onChange={handleChange}
        />
      </Box>

      {/* Spiritual Information */}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
        <Typography variant="h6">Spiritual Information</Typography>
        <FormControlLabel
          control={
            <Checkbox
              name="bibleStudy"
              checked={formData.bibleStudy}
              onChange={handleChange}
            />
          }
          label="Bible Study"
        />
        <FormControlLabel
          control={
            <Checkbox
              name="baptism"
              checked={formData.baptism}
              onChange={handleChange}
            />
          }
          label="Baptism"
        />
      </Box>

      <Button variant="contained" type="submit">
        Save Contact
      </Button>
    </Box>
  );
};

export default ContactForm;
